/** English spec label → emoji (aligned with catalogue HTML mockup). */
const EMOJI_BY_NAME = {
  "Max speed": "💨",
  Autonomy: "🔋",
  "Quick charger": "⚡",
  Speeds: "⚙️",
  "Charge time": "⏱",
  "Fast charge time": "⏱",
  "Max depth": "💧",
  Depth: "💧",
  Size: "📐",
  Weight: "⚖️",
  Material: "🔧",
  Volume: "📦",
  "Maximum load": "👤",
  "Minimum age": "⚠️",
  Surface: "🌊",
  Handles: "🖐️",
  Year: "📅",
};

function specIcon(name) {
  return EMOJI_BY_NAME[name] ?? "•";
}

/** Ordered Airtable field keys → English labels (first matches win; used when no per-toy map). */
const SPEC_FIELD_ROWS = [
  { key: "Vitesse max", name: "Max speed" },
  { key: "Autonomie", name: "Autonomy" },
  { key: "Chargeur rapide", name: "Quick charger" },
  { key: "Vitesses", name: "Speeds" },
  { key: "Temps de charge", name: "Charge time" },
  { key: "Temps de charge rapide", name: "Fast charge time" },
  { key: "Profondeur maximum", name: "Max depth" },
  { key: "Profondeur", name: "Depth" },
  { key: "Taille", name: "Size" },
  { key: "Poids", name: "Weight" },
  { key: "Matériau", name: "Material" },
  { key: "Volume", name: "Volume" },
  { key: "Charge maximum", name: "Maximum load" },
  { key: "Age minimum", name: "Minimum age" },
  { key: "Surface", name: "Surface" },
  { key: "Poignée", name: "Handles" },
  { key: "Année", name: "Year" },
];

function hasFieldValue(fields, key) {
  const v = fields?.[key];
  if (v == null) return false;
  if (typeof v === "string") return v.trim() !== "";
  return true;
}

function buildSpecsFromFields(fields) {
  return SPEC_FIELD_ROWS.filter((row) => hasFieldValue(fields, row.key)).map(
    (row) => ({
      icon: specIcon(row.name),
      name: row.name,
      value: fields[row.key],
    }),
  );
}

/**
 * Per-toy layout (same 4 fields as main branch).
 * `value` is the Airtable field name used to read `toy.fields[value]`.
 */
const specificationsByName = {
  "Seabob F9": [
    { name: "Max speed", value: "Vitesse max" },
    { name: "Autonomy", value: "Autonomie" },
    { name: "Quick charger", value: "Chargeur rapide" },
    { name: "Weight", value: "Poids" },
  ],
  "Seabob F5S": [
    { name: "Max speed", value: "Vitesse max" },
    { name: "Autonomy", value: "Autonomie" },
    { name: "Quick charger", value: "Chargeur rapide" },
    { name: "Weight", value: "Poids" },
  ],
  "Underwater scooter": [
    { name: "Max speed", value: "Vitesse max" },
    { name: "Autonomy", value: "Autonomie" },
    { name: "Quick charger", value: "Chargeur rapide" },
    { name: "Speeds", value: "Vitesses" },
  ],
  "E-Foil Fliteboard": [
    { name: "Autonomy", value: "Autonomie" },
    { name: "Quick charger", value: "Chargeur rapide" },
    { name: "Material", value: "Matériau" },
    { name: "Volume", value: "Volume" },
  ],
  "E-Foil Flite Air": [
    { name: "Autonomy", value: "Autonomie" },
    { name: "Quick charger", value: "Chargeur rapide" },
    { name: "Material", value: "Matériau" },
    { name: "Volume", value: "Volume" },
  ],
  "E-Foil Flitescooter": [
    { name: "Autonomy", value: "Autonomie" },
    { name: "Quick charger", value: "Chargeur rapide" },
    { name: "Material", value: "Matériau" },
    { name: "Volume", value: "Volume" },
  ],
  "E-Surf": [
    { name: "Max speed", value: "Vitesse max" },
    { name: "Autonomy", value: "Autonomie" },
    { name: "Quick charger", value: "Chargeur rapide" },
    { name: "Volume", value: "Volume" },
  ],
  Jetsurf: [
    { name: "Max speed", value: "Vitesse max" },
    { name: "Autonomy", value: "Autonomie" },
    { name: "Quick charger", value: "Chargeur rapide" },
    { name: "Volume", value: "Volume" },
  ],
  Paddle: [
    { name: "Size", value: "Taille" },
    { name: "Weight", value: "Poids" },
    { name: "Material", value: "Matériau" },
    { name: "Maximum load", value: "Charge maximum" },
  ],
  "Jellyfish Pool": [
    { name: "Size", value: "Taille" },
    { name: "Depth", value: "Profondeur" },
    { name: "Surface", value: "Surface" },
    { name: "Material", value: "Matériau" },
  ],
  "Platform 4m x 2m": [
    { name: "Size", value: "Taille" },
    { name: "Surface", value: "Surface" },
    { name: "Handles", value: "Poignée" },
    { name: "Material", value: "Matériau" },
  ],
  "Platform 2m x 2m": [
    { name: "Size", value: "Taille" },
    { name: "Surface", value: "Surface" },
    { name: "Handles", value: "Poignée" },
    { name: "Material", value: "Matériau" },
  ],
  "Floating Mat": [
    { name: "Size", value: "Taille" },
    { name: "Weight", value: "Poids" },
    { name: "Maximum load", value: "Charge maximum" },
    { name: "Material", value: "Matériau" },
  ],
};

function buildFromNameMapping(toy, rows) {
  return rows.map((spec) => ({
    icon: specIcon(spec.name),
    name: spec.name,
    value: toy.fields[spec.value],
  }));
}

export const getSpecifications = (toy) => {
  const mapped = specificationsByName[toy.fields["Name"]];
  if (mapped?.length) {
    return buildFromNameMapping(toy, mapped);
  }
  const fromFields = buildSpecsFromFields(toy.fields);
  if (fromFields.length > 0) {
    return fromFields.slice(0, 4);
  }
  return [];
};
